import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, ChevronRight, Utensils, Users, Loader2 } from 'lucide-react';
import { apiFetch } from '../../lib/api';

const HERMES_EASE = [0.22, 1, 0.36, 1] as const;

interface CommunityEvent {
  id: string;
  date: string;
  time: string;
  title: string;
  location: string;
  status: 'upcoming' | 'ongoing' | 'completed';
  meals: number;
  volunteers: number;
}

const statusConfig = {
  upcoming: { label: '即將舉辦', className: 'bg-green-100 text-green-700 border-green-200' },
  ongoing: { label: '進行中', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  completed: { label: '已完成', className: 'bg-gray-100 text-gray-600 border-gray-200' },
};

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const weekDay = d.toLocaleDateString('zh-HK', { weekday: 'short' });
  return { month, day, weekDay };
}

export default function EventSchedule() {
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [eventsRes, settingsRes] = await Promise.all([
        apiFetch<CommunityEvent[]>('/api/public/events'),
        apiFetch<Record<string, string>>('/api/public/settings'),
      ]);
      if (eventsRes.success && eventsRes.data) {
        setEvents(eventsRes.data);
      }
      if (settingsRes.success && settingsRes.data) {
        setSettings(settingsRes.data);
      }
      setLoading(false);
    })();
  }, []);

  return (
    <section className="section-padding bg-gradient-to-b from-orange-50/50 to-white">
      <div className="container-max mx-auto">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: HERMES_EASE }}
        >
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-bold mb-4">
            <Calendar className="w-4 h-4" />
            {settings.eventScheduleBadge || '社區飯堂場次'}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            {settings.eventScheduleTitle || '即將舉辦的社區活動'}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {settings.eventScheduleDescription || '每一場社區飯堂都是一次真實的改變。您的捐款正在支持這些活動的落地執行。'}
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400">{settings.eventScheduleEmpty || '暫無活動安排，敬請期待'}</p>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-4">
            {events.map((event, index) => {
              const { month, day, weekDay } = formatDate(event.date);
              const status = statusConfig[event.status] || statusConfig.upcoming;
              const isExpanded = expandedId === event.id;

              return (
                <motion.div
                  key={event.id}
                  className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
                    isExpanded ? 'border-brand-300 shadow-lg' : 'border-brand-100 shadow-sm hover:shadow-md'
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-30px' }}
                  transition={{ duration: 0.5, ease: HERMES_EASE, delay: index * 0.08 }}
                >
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : event.id)}
                    className="w-full flex items-center gap-4 p-4 sm:p-5 text-left"
                  >
                    {/* Date block */}
                    <div className="flex-shrink-0 w-16 h-16 bg-brand-50 rounded-xl flex flex-col items-center justify-center border border-brand-100">
                      <span className="text-xs text-brand-600 font-medium">{month}月</span>
                      <span className="text-xl font-extrabold text-brand-700">{day}</span>
                      <span className="text-[10px] text-brand-500">{weekDay}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate">
                          {event.title}
                        </h3>
                        <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${status.className}`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {event.time}
                        </span>
                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </span>
                      </div>
                    </div>

                    {/* Chevron */}
                    <ChevronRight
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                    />
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="px-4 sm:px-5 pb-5"
                    >
                      <div className="pt-3 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div className="bg-orange-50 rounded-xl p-3 text-center">
                          <Utensils className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                          <div className="text-lg font-bold text-gray-900">{event.meals}</div>
                          <div className="text-xs text-gray-500">預計飯餐數</div>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-3 text-center">
                          <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                          <div className="text-lg font-bold text-gray-900">{event.volunteers}</div>
                          <div className="text-xs text-gray-500">義工/學員人數</div>
                        </div>
                        <div className="bg-green-50 rounded-xl p-3 text-center col-span-2 sm:col-span-1">
                          <Calendar className="w-5 h-5 text-green-500 mx-auto mb-1" />
                          <div className="text-sm font-bold text-gray-900">{event.date}</div>
                          <div className="text-xs text-gray-500">活動日期</div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Call to action */}
        <motion.div
          className="text-center mt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-sm text-gray-500">
            {settings.eventScheduleCta || '更多場次陸續安排中，您的支持讓每一場活動成為可能'}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
